import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldInputProps, Field, FieldProps } from 'formik';
import React, { useMemo, useState } from 'react';
import { StringToggleTW } from 'tailwindComponents/toggleTW/StringToggleTS';
import { eFeePropertyNames } from 'types/order/fees/eFeePropertyNames';
import { DangerText, TextField, SelectField, Button, Select } from 'ui-toolkit';
import { DisbursementStatusPill } from '../../table/status/DisbursementStatusPill';
import { authorityOptions } from '../../utilities/authorityOptions';
import { calculateAmountCentsToDisburse } from '../disbursements/calculateAmountCentsToDisburse';
import { ConditionalFeeFields } from './ConditionalFeeFields';
import { adjustFeeToKeepSharedValues } from './adjustFeeToKeepSharedValues';
import { BuyerFields } from './fields/BuyerFields';
import { MathOnFieldChanges } from './fields/MathOnFieldChanges';
import { OthersFields } from './fields/OthersFields';
import { SellerFields } from './fields/SellerFields';
import { availablePropertiesBySelection } from './useAvailablePropertiesBySelection';
import { useGetDisbursementsForSettlementItem } from '../useGetDisbursementsForSettlementItem';
import { ICreateSettlementFeeForm } from 'types/order/fees/ICreateSettlementItemForm';
import { ISettlementAndFeeCategoryDefinitionData } from 'types/order/fees/ISettlementAndFeeCategoryDefinition';

export interface IFeeFormProps {
  baseName: string;
  fee: ICreateSettlementFeeForm;
  remove?: () => void;
  isReadOnly: boolean;
  options: ISettlementAndFeeCategoryDefinitionData[];
  payeeIsEditable: boolean;
  feeName: string;
  isRefinance: boolean;
  feeIndex?: number;
}

export const FeeForm: React.FC<IFeeFormProps> = ({
  baseName,
  fee,
  remove,
  isReadOnly,
  options,
  payeeIsEditable,
  feeName,
  feeIndex,
  isRefinance,
}) => {
  const properties = useMemo(
    () => availablePropertiesBySelection(options, fee.statementSettlementAndFeeCategoryDefinitionId, fee, isReadOnly),
    [options, fee, isReadOnly]
  );
  const feeTypeIdOptions = useMemo(
    () => options.map((t) => ({ label: t.feeCategory, value: t.statementSettlementAndFeeCategoryDefinitionId })),
    [options]
  );
  const [lock, setLock] = useState(true);
  const [categoryId, setCategoryId] = useState(fee.statementSettlementAndFeeCategoryDefinitionId);

  const findAndTrackCategory = (field: FieldInputProps<string>) => {
    const feeValue = feeTypeIdOptions.find((t) => field.value === t.value);
    if (feeValue?.value !== categoryId) setCategoryId(feeValue?.value);
    return feeValue;
  };

  const disbursementIds = useMemo(() => [fee.disbursementIds], [fee.disbursementIds]);
  const [disbursements] = useGetDisbursementsForSettlementItem(disbursementIds);
  const showIncomplete =
    disbursements.length === 0 &&
    calculateAmountCentsToDisburse(fee.numericFeePropertyValues, fee.stringFeePropertyValues) > 0;

  return (
    <>
      <MathOnFieldChanges baseName={baseName} properties={properties} />
      {properties.canAddMoreFees && (
        <div className={'flex basis-full flex-row gap-4 p-1'}>
          <div className={'flex-grow text-xl font-semibold'}>
            Fee {feeIndex} - {feeName}
          </div>
          {showIncomplete && <DisbursementStatusPill disbursements={disbursements} isFeeDisplay />}
          {remove && (
            <Button isInline intent={'ghost'} onClick={remove}>
              <DangerText>
                <FontAwesomeIcon icon={faTrashAlt} />
              </DangerText>
            </Button>
          )}
        </div>
      )}
      <div className={'flex basis-full flex-row'}>
        {properties[eFeePropertyNames.NettedOut] && (
          <StringToggleTW
            readOnly={isReadOnly}
            className={'basis-30 p-1'}
            text={'Netted out'}
            name={`${baseName}.stringFeePropertyValues.${eFeePropertyNames.NettedOut}`}
          />
        )}
        {(properties[eFeePropertyNames.Payee] || payeeIsEditable) && (
          <TextField
            readOnly={isReadOnly && !payeeIsEditable}
            className={'flex-grow basis-2/6 p-1'}
            name={`${baseName}.stringFeePropertyValues.${eFeePropertyNames.Payee}`}
            label={`Payee Name`}
            placeholder={'Add Name'}
          />
        )}
        {properties[eFeePropertyNames.Authority] && (
          <Field name={`${baseName}.stringFeePropertyValues.${eFeePropertyNames.Authority}`}>
            {({ field }: FieldProps) => (
              <SelectField
                className={'flex-grow basis-2/6 p-1'}
                label={'Authority'}
                options={authorityOptions}
                name={`${baseName}.stringFeePropertyValues.${eFeePropertyNames.Authority}`}
                isClearable={false}
                isDisabled={isReadOnly || !properties[eFeePropertyNames.Authority]}
                value={field.value}
              />
            )}
          </Field>
        )}
        {properties.canAddMoreFees && (
          <Field name={`${baseName}.statementSettlementAndFeeCategoryDefinitionId`}>
            {({ field, form }: FieldProps<string>) => (
              <Select
                {...field}
                action={
                  <Button isInline intent={'ghost'} onClick={() => setLock(!lock)}>
                    <DangerText>{lock ? 'Unlock' : 'Lock'}</DangerText>
                  </Button>
                }
                className={'flex-grow basis-6/12 p-1'}
                value={findAndTrackCategory(field)}
                label={'Fee category'}
                options={feeTypeIdOptions}
                onChange={(value: any, { action }: any) => {
                  if (action === 'select-option') {
                    form.setFieldValue(field.name, value?.value);
                    const feePropertyValues = adjustFeeToKeepSharedValues({
                      numericFeePropertyValues: fee.numericFeePropertyValues,
                      oldDefinitionId: field.value,
                      newDefinitionId: value?.value,
                      options,
                    });
                    form.setFieldValue(`${baseName}.numericFeePropertyValues`, feePropertyValues);
                    setCategoryId(value.value);
                  }
                }}
                isDisabled={isReadOnly || lock}
              />
            )}
          </Field>
        )}
      </div>

      <div className={'flex basis-full flex-row flex-wrap'}>
        <ConditionalFeeFields properties={properties} baseName={baseName} fee={fee} readOnly={isReadOnly} />
      </div>
      <div className={'flex basis-full flex-row flex-wrap'}>
        <BuyerFields properties={properties} baseName={baseName} isRefinance={isRefinance} readOnly={isReadOnly} />
        {!isRefinance && <SellerFields properties={properties} baseName={baseName} readOnly={isReadOnly} />}
      </div>
      <div className={'flex basis-full flex-row flex-wrap'}>
        <OthersFields properties={properties} baseName={baseName} readOnly={isReadOnly} />
      </div>
    </>
  );
};
